import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import './CollegeAlgebra.css';

// Lesson content supports a minimal, deliberately narrow markdown subset --
// just **bold** for emphasizing a key term or warning inline. Rendered as
// real <strong> elements (never dangerouslySetInnerHTML), so this can't
// introduce an XSS surface even though the content comes from the API.
function renderInline(text) {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );
}

function LessonBlock({ block, index }) {
  switch (block.type) {
    case 'list':
      return (
        <div className="ca-lesson-block" key={index}>
          {block.intro && <p className="ca-lesson-paragraph">{renderInline(block.intro)}</p>}
          <ul className="ca-lesson-list-block">
            {block.items.map((item, i) => (
              <li key={i}>{renderInline(item)}</li>
            ))}
          </ul>
        </div>
      );
    case 'strategy':
      return (
        <div className="ca-lesson-block ca-lesson-strategy" key={index}>
          <span className="ca-lesson-strategy-label">{block.title || 'How to approach these problems'}</span>
          <ol className="ca-lesson-strategy-steps">
            {block.steps.map((step, i) => (
              <li key={i}>{renderInline(step)}</li>
            ))}
          </ol>
        </div>
      );
    case 'steps':
      return (
        <ol className="ca-lesson-steps-block" key={index}>
          {block.steps.map((step, i) => (
            <li key={i}>{renderInline(step)}</li>
          ))}
        </ol>
      );
    case 'example':
      return (
        <div className="ca-lesson-block ca-lesson-example" key={index}>
          <span className="ca-lesson-example-label">Worked example</span>
          <p className="ca-lesson-example-prompt">{renderInline(block.prompt)}</p>
          <ol className="ca-lesson-steps-block">
            {block.steps.map((step, i) => (
              <li key={i}>{renderInline(step)}</li>
            ))}
          </ol>
          {block.answer && <p className="ca-lesson-example-answer">{renderInline(block.answer)}</p>}
          {block.khanAcademyUrl && (
            <a
              className="ca-lesson-example-khan-link"
              href={block.khanAcademyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch on Khan Academy{block.khanAcademyTitle ? `: ${block.khanAcademyTitle}` : ''} &rarr;
            </a>
          )}
        </div>
      );
    case 'paragraph':
    default:
      return (
        <p className="ca-lesson-paragraph" key={index}>
          {renderInline(block.text)}
        </p>
      );
  }
}

export default function LessonViewer() {
  const { slug } = useParams();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setLesson(null);
    setError('');
    api
      .get(`/api/college-algebra/lessons/${slug}`)
      .then(setLesson)
      .catch(() => setError('Unable to load this lesson right now.'));
  }, [slug]);

  if (error) return <p className="ca-error">{error}</p>;
  if (!lesson) return <p>Loading lesson&hellip;</p>;

  return (
    <div className="ca-page">
      <Link to={`/college-algebra/units/${lesson.unit_slug}`} className="ca-back-link">
        &larr; Back to {lesson.unit_name}
      </Link>
      <h1>{lesson.title}</h1>
      <div className="ca-lesson-content">
        {lesson.content.map((block, index) => (
          <LessonBlock block={block} index={index} key={index} />
        ))}
      </div>
    </div>
  );
}
